import { useEffect, useRef, useState } from 'react';
import {
  createEyeCode, getEyeSettings, saveEyeSettings, getEyeSites, revokeEyeSite, importEyeCsv,
  getEyeImports, deleteEyeImport, getEyeDomains, getEyeCodes, deleteEyeCode, getEyeUsage,
  type EyeSettings, type EyeSite, type EyeWidgets, type EyeImportKind, type EyeImportResult,
  type EyeImport, type EyeDomain, type EyePendingCode, type EyeUsage,
} from './adminApi';

/**
 * Clients → [klient] → AI EYE (D-36 §3).
 *
 * Tri veci, ktoré Jan potrebuje na jednom mieste: vygenerovať párovací kód,
 * zapnúť/vypnúť widgety, a nahrať týždenné exporty. Plus zoznam spárovaných
 * stránok s revoke — jediné miesto, kde sa dá ukradnutý token zneplatniť.
 *
 * Klient tu nemá čo nastavovať: v plugine nie sú žiadne prepínače, sú tu.
 */

const WIDGETS: { key: keyof EyeWidgets; label: string; hint: string }[] = [
  { key: 'readiness', label: 'Readiness', hint: 'Skóre + per-page' },
  { key: 'visibility', label: 'Visibility', hint: 'named / cited / absent per otázka' },
  { key: 'knowsYou', label: 'Knows you', hint: 'Čo model vie z tréningu, bez stránky' },
  { key: 'trend', label: 'Trend', hint: 'Probe % + Bing citácie + GSC impresie' },
  { key: 'competitors', label: 'Competitors', hint: '„Who wins instead", zapni, len keď to klientovi pomôže' },
  { key: 'bing', label: 'Bing AI Performance', hint: 'Copilot citácie z importu' },
  { key: 'gsc', label: 'Google Search Console', hint: 'Impresie, pozícia, kliky z importu' },
  { key: 'fixit', label: 'Fix it', hint: 'Plán (len Pro, paywall je serverový)' },
  { key: 'nextScan', label: 'Next scan', hint: 'Odpočet do ďalšieho behu' },
  { key: 'weeklySentences', label: 'Weekly sentences', hint: '„This week", tri vety ľudskou rečou' },
];

const IMPORTS: { kind: EyeImportKind; label: string; hint: string; needsDate: boolean }[] = [
  { kind: 'bing_ai', label: 'Bing AI Performance', hint: 'AIPerformanceOverviewStats…csv, denné citácie', needsDate: false },
  { kind: 'gsc_chart', label: 'GSC Chart', hint: 'Chart.csv, denné kliky a impresie', needsDate: false },
  { kind: 'gsc_queries', label: 'GSC Queries', hint: 'Queries.csv, súhrn za obdobie, nie po dňoch', needsDate: true },
  { kind: 'gsc_pages', label: 'GSC Pages', hint: 'Pages.csv, súhrn za obdobie, nie po dňoch', needsDate: true },
];

const ago = (iso: string | null): string => {
  if (!iso) return 'nikdy';
  const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return 'pred chvíľou';
  if (m < 60) return `pred ${m} min`;
  if (m < 60 * 24) return `pred ${Math.round(m / 60)} h`;
  return new Date(iso).toLocaleDateString();
};

export default function EyePanel({ token, customerId, email, onClose }: {
  token: string; customerId: string; email: string; onClose: () => void;
}): JSX.Element {
  const [settings, setSettings] = useState<EyeSettings | null>(null);
  const [sites, setSites] = useState<EyeSite[]>([]);
  const [code, setCode] = useState<{ code: string; expiresAt: string; domain: string | null; allowedSite: string | null } | null>(null);
  // Which measured domain the install will show, and optionally which site may
  // redeem the code. These were one thing — site_url both identified the install
  // AND decided what it could read — which made a staging or local install
  // impossible: it paired and then showed an empty dashboard.
  const [domains, setDomains] = useState<EyeDomain[]>([]);
  // Several sites means several codes outstanding at once. One code per client
  // was a real limit the moment an account had two WordPress installs.
  const [codes, setCodes] = useState<EyePendingCode[]>([]);
  const [copiedId, setCopiedId] = useState<number | null>(null);
  // Whether this client actually opens the plugin. last_seen_at only says the
  // install is alive, which is a different and much weaker claim.
  const [usage, setUsage] = useState<EyeUsage | null>(null);
  const [pickDomain, setPickDomain] = useState<string>('');
  const [pinSite, setPinSite] = useState<string>('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imported, setImported] = useState<{ kind: EyeImportKind; r: EyeImportResult } | null>(null);
  const [imports, setImports] = useState<EyeImport[]>([]);
  const [periodEnd, setPeriodEnd] = useState(() => new Date().toISOString().slice(0, 10));
  const fileRef = useRef<HTMLInputElement>(null);
  const pending = useRef<EyeImportKind | null>(null);

  const load = async (): Promise<void> => {
    const [s, t, im, dm, cs] = await Promise.all([
      getEyeSettings(token, customerId), getEyeSites(token, customerId), getEyeImports(token, customerId),
      getEyeDomains(token, customerId), getEyeCodes(token, customerId),
    ]);
    setSettings(s); setSites(t); setImports(im); setDomains(dm); setCodes(cs);
    // Not in the Promise.all: usage is nice to have, and a client with no views
    // must not be able to blank the whole panel.
    void getEyeUsage(token, customerId).then(setUsage).catch(() => setUsage(null));
    // One measured domain means there is nothing to choose — preselect it rather
    // than making the operator pick the only option.
    if (dm.length === 1 && dm[0]) setPickDomain(dm[0].domain);
  };
  useEffect(() => { load().catch((e) => setError(e instanceof Error ? e.message : 'load failed')); },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerId]);

  const run = (fn: () => Promise<void>) => async (): Promise<void> => {
    setBusy(true); setError(null);
    try { await fn(); } catch (e) { setError(e instanceof Error ? e.message : 'failed'); } finally { setBusy(false); }
  };

  async function toggle(key: keyof EyeWidgets): Promise<void> {
    if (!settings) return;
    const next = !settings.widgets[key];
    // Optimisticky, ale so serverovou odpoveďou ako pravdou — inak by prepínač
    // ukazoval stav, ktorý sa neuložil.
    setSettings({ ...settings, widgets: { ...settings.widgets, [key]: next } });
    const saved = await saveEyeSettings(token, customerId, { widgets: { [key]: next } });
    setSettings(saved);
  }

  function pickFile(kind: EyeImportKind): void {
    pending.current = kind;
    fileRef.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0];
    const kind = pending.current;
    e.target.value = ''; // aby sa dal ten istý súbor nahrať znova
    if (!file || !kind) return;
    setBusy(true); setError(null); setImported(null);
    try {
      const csv = await file.text();
      const needsDate = IMPORTS.find((i) => i.kind === kind)?.needsDate ?? false;
      const r = await importEyeCsv(token, customerId, kind, csv, needsDate ? periodEnd : undefined, file.name);
      setImported({ kind, r });
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'import failed');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start justify-center overflow-y-auto p-4" onClick={onClose}>
      <div className="w-full max-w-3xl bg-warm-dark border border-warm-dark-2 rounded-xl p-6 my-8" onClick={(ev) => ev.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <h3 className="font-serif text-xl">AI EYE</h3>
            <p className="font-mono text-sm text-text-secondary">{email}</p>
          </div>
          <button onClick={onClose} className="font-mono text-sm text-text-secondary hover:text-cyan">zavrieť ✕</button>
        </div>

        {error && <p className="font-mono text-sm text-[#E0574A] border border-[#E0574A]/40 rounded-lg px-3 py-2 mb-4">{error}</p>}

        {/* 1 — párovací kód */}
        <section className="border border-warm-dark-2 rounded-lg p-4 mb-4">
          <h4 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-2">Párovanie</h4>
          <p className="text-sm text-text-secondary mb-3">
            Klient zadá kód v plugine po aktivácii. Platí 15 minút a dá sa použiť raz.
            Vygeneruj ich koľko potrebuješ, jeden na každý web.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="font-mono text-xs uppercase tracking-widest text-text-secondary">Ktorú doménu zobraziť</span>
              <select
                value={pickDomain}
                onChange={(e) => setPickDomain(e.target.value)}
                className="mt-1 w-full bg-warm-dark border border-warm-dark-2 rounded-lg px-3 py-2 text-sm font-mono"
              >
                <option value="">stránka musí byť na meranej doméne</option>
                {domains.map((d) => (
                  <option key={d.domain} value={d.domain}>
                    {d.domain} ({d.runs} {d.runs === 1 ? 'beh' : 'behov'})
                  </option>
                ))}
              </select>
              {/* A list, never free text: you cannot mistype a domain, and you
                  cannot name one this client has never scanned. */}
              <span className="block text-xs text-text-secondary mt-1">
                Len domény, ktoré tento klient naozaj meral.
              </span>
            </label>

            <label className="block">
              <span className="font-mono text-xs uppercase tracking-widest text-text-secondary">Kde bude plugin (voliteľné)</span>
              <input
                value={pinSite}
                onChange={(e) => setPinSite(e.target.value)}
                placeholder="napr. konex.test"
                disabled={!pickDomain}
                className="mt-1 w-full bg-warm-dark border border-warm-dark-2 rounded-lg px-3 py-2 text-sm font-mono disabled:opacity-40"
              />
              <span className="block text-xs text-text-secondary mt-1">
                {pickDomain
                  ? 'Prázdne = kód použije ktorákoľvek stránka. Vyplnené = len táto.'
                  : 'Dá sa vyplniť, až keď vyberieš doménu.'}
              </span>
            </label>
          </div>

          {pickDomain && (
            <p className="text-sm text-text-secondary mt-3 border-l-2 border-cyan/40 pl-3">
              Plugin bude ukazovať <span className="text-text-primary font-mono">{pickDomain}</span>
              {pinSite ? <> a spárovať sa bude dať len z <span className="text-text-primary font-mono">{pinSite}</span>.</>
                : <>, nech beží kdekoľvek. Bez pripnutej stránky stačí na spárovanie samotný kód, drž ho teda ako heslo.</>}
            </p>
          )}

          <button
            onClick={run(async () => {
              setCode(await createEyeCode(token, customerId, {
                domain: pickDomain || null,
                allowedSite: pickDomain ? (pinSite.trim() || null) : null,
              }));
              setCodes(await getEyeCodes(token, customerId));
            })}
            disabled={busy}
            className="mt-3 bg-cyan text-warm-dark font-medium rounded-lg px-4 py-2 text-sm hover:opacity-90 disabled:opacity-40 transition"
          >{codes.length ? 'Vygenerovať ďalší' : 'Vygenerovať kód'}</button>

          {/* Every outstanding code, not just the last one — with several sites
              to connect, the one on screen was the only one you could see. */}
          {codes.length > 0 && (
            <div className="mt-4 border-t border-warm-dark-2 pt-4">
              <h5 className="font-mono text-xs uppercase tracking-widest text-text-secondary mb-2">
                Nepoužité kódy ({codes.length})
              </h5>
              <ul className="space-y-2">
                {codes.map((c) => (
                  <li
                    key={c.id}
                    className={`flex items-center gap-3 flex-wrap rounded-lg px-3 py-2 border ${
                      c.expired ? 'border-warm-dark-2 opacity-50' : 'border-cyan/30'
                    } ${code?.code === c.code ? 'bg-cyan/5' : ''}`}
                  >
                    <code className={`font-mono text-lg tracking-widest ${c.expired ? 'text-text-secondary line-through' : 'text-cyan'}`}>
                      {c.code}
                    </code>
                    <span className="text-xs text-text-secondary flex-1 min-w-[12rem]">
                      {c.expired
                        ? 'vypršal'
                        : <>platí do {new Date(c.expiresAt).toLocaleTimeString()}</>}
                      {c.domain
                        ? <> · ukáže <span className="text-text-primary font-mono">{c.domain}</span>
                            {c.allowedSite && <> · len z <span className="text-text-primary font-mono">{c.allowedSite}</span></>}</>
                        : <> · len z meranej domény</>}
                    </span>
                    {!c.expired && (
                      <button
                        onClick={() => { void navigator.clipboard.writeText(c.code).then(() => { setCopiedId(c.id); setTimeout(() => setCopiedId(null), 1500); }); }}
                        className="font-mono text-xs text-cyan border border-cyan/40 rounded px-2 py-1 hover:bg-cyan/10 transition"
                      >{copiedId === c.id ? 'skopírované ✓' : 'kopírovať'}</button>
                    )}
                    <button
                      onClick={run(async () => {
                        await deleteEyeCode(token, customerId, c.id);
                        setCodes(await getEyeCodes(token, customerId));
                      })}
                      disabled={busy}
                      className="font-mono text-xs text-[#E0574A] border border-[#E0574A]/40 rounded px-2 py-1 hover:bg-[#E0574A]/10 disabled:opacity-40 transition"
                    >zmazať</button>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-text-secondary mt-2">
                Mazanie sa týka len nepoužitých kódov. Odpojiť už spárovaný web sa dá nižšie v „Spárované stránky".
              </p>
            </div>
          )}
        </section>

        {/* 2 — používanie: otvára to vôbec? */}
        <section className="border border-warm-dark-2 rounded-lg p-4 mb-4">
          <h4 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Používanie</h4>
          {!usage || usage.totalViews === 0 ? (
            <p className="text-sm text-text-secondary">
              {sites.length
                ? 'Spárované, ale plugin zatiaľ nikto neotvoril. To je najužitočnejší riadok na tejto obrazovke.'
                : 'Žiadna spárovaná stránka.'}
            </p>
          ) : (
            <>
              <div className="flex flex-wrap gap-5 mb-3">
                <div>
                  <div className="font-mono text-2xl text-cyan">{usage.totalViews}</div>
                  <div className="font-mono text-xs text-text-secondary">otvorení spolu</div>
                </div>
                <div>
                  <div className="font-mono text-2xl text-text-primary">{usage.activeDays}</div>
                  <div className="font-mono text-xs text-text-secondary">dní s aktivitou</div>
                </div>
                <div>
                  <div className="font-mono text-sm text-text-primary mt-2">
                    {usage.lastAt ? new Date(usage.lastAt).toLocaleString() : '-'}
                  </div>
                  <div className="font-mono text-xs text-text-secondary">naposledy</div>
                </div>
              </div>

              <p className="font-mono text-xs uppercase tracking-widest text-text-secondary/70 mb-1">Čo otvára</p>
              <div className="flex flex-wrap gap-2 mb-3">
                {usage.screens.map((sc) => (
                  <span key={sc.screen} className="font-mono text-xs border border-warm-dark-2 rounded px-2 py-1 text-text-secondary">
                    {sc.screen}<span className="text-cyan/80"> ×{sc.views}</span>
                  </span>
                ))}
              </div>

              {/* A sparkline would need a chart; a row of days says the same thing:
                  is it still happening, and how regularly. */}
              <p className="font-mono text-xs uppercase tracking-widest text-text-secondary/70 mb-1">Posledné dni</p>
              <div className="flex flex-wrap gap-1">
                {usage.days.slice(0, 30).map((dd) => (
                  <span key={dd.day} title={`${dd.day}: ${dd.views}`}
                    className="font-mono text-[10px] border border-warm-dark-2 rounded px-1.5 py-0.5 text-text-secondary">
                    {dd.day.slice(5)}<span className="text-cyan/70"> {dd.views}</span>
                  </span>
                ))}
              </div>
              <p className="text-xs text-text-secondary mt-3">
                Počítajú sa len obrazovky, ktoré človek otvorí. Párovanie a sťahovanie sú udalosti, nie návštevy.
                Neukladá sa žiadny používateľ ani IP, vo WordPresse môže byť administrátorov viac a ktorý klikol,
                nás nemá čo zaujímať.
              </p>
            </>
          )}
        </section>

        {/* 3 — widgety */}
        <section className="border border-warm-dark-2 rounded-lg p-4 mb-4">
          <h4 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Čo klient vidí</h4>
          <p className="text-sm text-text-secondary mb-3">
            Vypnutý widget sa v plugine nerenderuje vôbec. A server jeho dáta ani neposiela.
          </p>
          {settings ? (
            <div className="grid sm:grid-cols-2 gap-2">
              {WIDGETS.map((w) => (
                <label key={w.key} className="flex items-start gap-2 border border-warm-dark-2 rounded-lg px-3 py-2 cursor-pointer hover:border-cyan/40 transition">
                  <input type="checkbox" checked={settings.widgets[w.key]} onChange={() => void toggle(w.key)} disabled={busy} className="accent-cyan mt-0.5" />
                  <span className="min-w-0">
                    <span className="text-sm text-text-primary block">{w.label}</span>
                    <span className="text-sm text-text-secondary block leading-snug">{w.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          ) : <p className="font-mono text-sm text-text-secondary">načítavam…</p>}

          {settings && (
            <div className="flex items-center gap-2 mt-3">
              <span className="font-mono text-sm text-text-secondary">Jazyk pluginu</span>
              {(['en', 'nl'] as const).map((l) => (
                <button key={l} onClick={run(async () => setSettings(await saveEyeSettings(token, customerId, { lang: l })))}
                  disabled={busy}
                  className={`font-mono text-xs rounded px-3 py-1.5 border transition ${settings.lang === l ? 'bg-cyan/10 text-cyan border-cyan/60' : 'text-text-secondary border-[#70707D] hover:text-cyan'}`}>
                  {l.toUpperCase()}
                </button>
              ))}
            </div>
          )}
        </section>

        {/* 3 — importy */}
        <section className="border border-warm-dark-2 rounded-lg p-4 mb-4">
          <h4 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">Týždenné importy</h4>
          <p className="text-sm text-text-secondary mb-3">
            Ten istý súbor nahratý dvakrát čísla <span className="text-text-primary">nezdvojnásobí</span>, import
            prepisuje, nepričítava. Každé nahratie sa aj tak zapíše do histórie nižšie a dá sa vziať späť.
            <br />
            <span className="text-estimated">Obsah exportu neobsahuje doménu</span>, takže súbor nahratý pod zlého
            klienta server odhaliť nevie, preto tá história.
          </p>
          <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={(e) => void onFile(e)} className="hidden" />
          <div className="grid sm:grid-cols-2 gap-2">
            {IMPORTS.map((i) => (
              <button key={i.kind} onClick={() => pickFile(i.kind)} disabled={busy}
                className="text-left border border-warm-dark-2 rounded-lg px-3 py-2 hover:border-cyan/40 disabled:opacity-40 transition">
                <span className="text-sm text-text-primary block">{i.label}</span>
                <span className="text-sm text-text-secondary block leading-snug">{i.hint}</span>
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 mt-3">
            <span className="font-mono text-sm text-text-secondary">Koniec obdobia</span>
            <input type="date" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)}
              className="bg-warm-dark-2 border border-warm-dark-2 focus:border-cyan/60 rounded px-2 py-1 text-text-primary outline-none font-mono text-sm" />
            <span className="text-sm text-text-secondary">platí pre Queries a Pages, ktoré dátum neobsahujú</span>
          </label>
          {imported && (
            <div className="mt-3 border border-success/40 bg-success/5 rounded-lg px-3 py-2">
              <p className="text-sm text-text-primary">
                {IMPORTS.find((i) => i.kind === imported.kind)?.label}: zapísaných <span className="font-mono">{imported.r.written}</span> riadkov
                z <span className="font-mono">{imported.r.parsed}</span>.
              </p>
              {imported.r.duplicateOf !== null && (
                <p className="text-sm text-estimated mt-1">
                  Presne tento súbor tu už raz bol (import #{imported.r.duplicateOf}). Hodnoty sú rovnaké, nič sa
                  nezdvojilo, ale ak si čakal zmenu, je to zrejme starý export.
                </p>
              )}
              {imported.r.notes.map((n) => <p key={n} className="text-sm text-estimated mt-1">{n}</p>)}
            </div>
          )}
        </section>

        {/* 3b — história nahratí */}
        <section className="border border-warm-dark-2 rounded-lg p-4 mb-4">
          <h4 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-1">História nahratí</h4>
          <p className="text-sm text-text-secondary mb-3">
            Každé nahratie je samostatný záznam. Hodnoty za ten istý deň sa prepisujú, sú to fakty o dňoch, nie
            o súboroch, ale <span className="text-text-primary">zmazať import sa dá</span>, a zmaže aj fakty, ktoré zapísal.
          </p>
          {imports.length === 0 ? (
            <p className="text-sm text-text-secondary">Zatiaľ nič nahraté.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-text-secondary font-mono text-xs text-left">
                <th className="py-1 pr-3">Kedy</th><th className="pr-3">Typ</th><th className="pr-3">Súbor</th>
                <th className="pr-3">Obdobie</th><th className="pr-3">Faktov</th><th></th>
              </tr></thead>
              <tbody>
                {imports.map((im) => (
                  <tr key={im.id} className="border-t border-warm-dark-2">
                    <td className="py-2 pr-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                      {new Date(im.createdAt).toLocaleString()}
                    </td>
                    <td className="pr-3 font-mono text-xs text-cyan/80">{im.kind}</td>
                    <td className="pr-3 text-text-primary truncate max-w-[14rem]" title={im.filename ?? ''}>{im.filename ?? '-'}</td>
                    <td className="pr-3 font-mono text-xs text-text-secondary whitespace-nowrap">
                      {im.periodFrom && im.periodTo ? `${im.periodFrom} → ${im.periodTo}` : '-'}
                    </td>
                    <td className="pr-3 font-mono text-xs">
                      <span className="text-text-primary">{im.rowsLive}</span>
                      <span className="text-text-secondary/60"> / {im.rowsWritten}</span>
                    </td>
                    <td>
                      <button
                        onClick={run(async () => {
                          if (!window.confirm(
                            `Zmazať import z ${new Date(im.createdAt).toLocaleString()}?\n\n`
                            + `Zmaže sa aj ${im.rowsLive} faktov, ktoré zapísal. Hodnoty, ktoré tento import prepísal, `
                            + `sa NEOBNOVIA, ak ich chceš späť, nahraj starší súbor znova.`,
                          )) return;
                          const r = await deleteEyeImport(token, customerId, im.id);
                          setImported(null);
                          await load();
                          if (r.factsRemoved === 0) setError('Import zmazaný, ale žiadne fakty mu už nepatrili, prepísal ich novší import.');
                        })}
                        disabled={busy}
                        className="font-mono text-xs text-text-secondary/60 hover:text-[#E0574A] transition"
                      >zmazať</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="font-mono text-xs text-text-secondary/70 mt-2">
            Stĺpec „Faktov" je <span className="text-text-primary">koľko z nich ešte platí</span> / koľko ich import
            zapísal. Rozdiel znamená, že novší import tie dni prepísal.
          </p>
        </section>

        {/* 4 — spárované stránky */}
        <section className="border border-warm-dark-2 rounded-lg p-4">
          <h4 className="font-mono text-sm uppercase tracking-widest text-text-secondary mb-2">Spárované stránky</h4>
          {sites.length === 0 ? (
            <p className="text-sm text-text-secondary">Zatiaľ žiadna. Vygeneruj kód a pošli ho klientovi.</p>
          ) : (
            <table className="w-full text-sm">
              <thead><tr className="text-text-secondary font-mono text-xs text-left">
                <th className="py-1 pr-3">Stránka</th><th className="pr-3">WP / plugin</th>
                <th className="pr-3">Práva</th><th className="pr-3">Posledný sync</th><th></th>
              </tr></thead>
              <tbody>
                {sites.map((s) => (
                  <tr key={s.id} className="border-t border-warm-dark-2">
                    <td className="py-2 pr-3 text-text-primary truncate max-w-[16rem]">{s.siteUrl ?? '-'}</td>
                    <td className="pr-3 font-mono text-xs text-text-secondary">{s.wpVersion ?? '?'} / {s.pluginVersion ?? '?'}</td>
                    <td className="pr-3 font-mono text-xs text-text-secondary">{s.scopes.join(', ')}</td>
                    <td className="pr-3 font-mono text-xs text-text-secondary">{ago(s.lastSeenAt)}</td>
                    <td>
                      <button
                        onClick={run(async () => {
                          if (!window.confirm(`Odpárovať ${s.siteUrl}? Plugin okamžite stratí prístup a klient sa bude musieť spárovať znova.`)) return;
                          await revokeEyeSite(token, customerId, s.id);
                          await load();
                        })}
                        disabled={busy}
                        className="font-mono text-xs text-text-secondary/60 hover:text-[#E0574A] transition"
                      >revoke</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="font-mono text-xs text-text-secondary/70 mt-2">
            Revoke je jediný spôsob, ako zneplatniť ukradnutý token, klient si vie odpárovať aj sám v plugine.
          </p>
        </section>
      </div>
    </div>
  );
}
